import { useRef, useState, type ChangeEvent } from 'react'
import { pointerToBearing, snapToFive } from './headingDial/geometry'

/// Graphical heading-bug dial. Replaces the numeric heading input
/// in CommandPanel. The bug's working value lives in local state
/// (initialized from `targetHeadingDeg`); commit buttons fire
/// `onSubmit(bug, direction)`.
///
/// IMPORTANT: parent must pass `key={aircraft.id}` so React remounts
/// the component on aircraft selection change. That's how the bug
/// resets to the new aircraft's target heading. (Equivalent to a
/// useEffect([aircraft.id]) reset; the key is simpler and local.)
export interface HeadingDialProps {
  currentHeadingDeg: number
  targetHeadingDeg: number
  onSubmit: (deg: number, direction: 'shortest' | 'left' | 'right') => void
}

// Sizing tokens: documented here for reference; actual sizing via Tailwind classes.
// Exported so they're tree-shakeable but not flagged unused by tsc.
export const DIAL_SIZE_DESKTOP = 140 // px
export const DIAL_SIZE_PHONE = 180 // px

export function HeadingDial(props: HeadingDialProps) {
  // Snap the initial bug value too. Without this, dirty would
  // immediately be true when the aircraft's actual targetHeadingDeg
  // is a non-multiple-of-5 float (e.g. 267.3 from the sim) because
  // every interaction path snaps to 5. Mount-then-snap collapses
  // that gap so the commit buttons start disabled as intended.
  const [bug, setBug] = useState<number>(snapToFive(props.targetHeadingDeg))
  const [rawInput, setRawInput] = useState<string | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)

  const dirty = bug !== snapToFive(props.targetHeadingDeg)

  /// Convert a pointer event to a snapped bearing using the SVG's
  /// own bounding rect — works at any DPR / zoom because we're
  /// operating in client space, with the dial's center always at
  /// (rect.left + rect.width/2, rect.top + rect.height/2).
  function bearingFromEvent(e: React.PointerEvent<SVGSVGElement>): number {
    const svg = svgRef.current
    if (!svg) return bug
    const rect = svg.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    return snapToFive(pointerToBearing(e.clientX, e.clientY, cx, cy))
  }

  function handlePointerDown(e: React.PointerEvent<SVGSVGElement>) {
    // Capture so subsequent moves/up always come back to us, even if
    // the pointer leaves the SVG box.
    e.currentTarget.setPointerCapture(e.pointerId)
    setBug(bearingFromEvent(e))
  }

  function handlePointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
    setBug(bearingFromEvent(e))
  }

  function handlePointerUp(e: React.PointerEvent<SVGSVGElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    // Note: releasing the pointer does NOT fire a command. The
    // player commits explicitly via Confirm / Turn left / Turn right.
  }

  const inputRef = useRef<HTMLInputElement | null>(null)

  function focusNumericInput() {
    inputRef.current?.focus()
    inputRef.current?.select()
  }

  function handleNumericInputChange(e: ChangeEvent<HTMLInputElement>) {
    // Buffer the raw text while the user is typing. We commit on blur
    // (or Enter); committing on every keystroke would snap the value
    // after the first digit and block any 2- or 3-digit entry.
    const raw = e.target.value.replace(/\D/g, '').slice(0, 3)
    setRawInput(raw)
  }

  function commitNumericInput() {
    if (rawInput === null) return
    if (rawInput === '') {
      setRawInput(null)
      return
    }
    const n = parseInt(rawInput, 10)
    if (Number.isFinite(n)) {
      setBug(snapToFive(((n % 360) + 360) % 360))
    }
    setRawInput(null)
  }

  return (
    <div className="relative flex flex-col items-center">
      {/* Hidden numeric input sits behind the readout. Tapping the
          readout focuses it, which triggers the native iOS/Android
          numeric keyboard. inputMode="numeric" + type="text" is the
          most reliable pattern across browsers — type="number" has
          known issues with leading-zero handling and Safari quirks. */}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={rawInput ?? String(bug).padStart(3, '0')}
        onChange={handleNumericInputChange}
        onBlur={commitNumericInput}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // Triggers onBlur via focus loss, which commits.
            inputRef.current?.blur()
          }
        }}
        onFocus={(e) => e.target.select()}
        className="absolute left-1/2 top-[80px] -translate-x-1/2 w-[72px] h-[40px] opacity-0 pointer-events-none"
        aria-label="Heading degrees"
        tabIndex={-1}
      />
      {/* Two sizing tokens: 180px on phone, 140px on desktop (md+) */}
      <svg
        ref={svgRef}
        viewBox="0 0 200 200"
        className="size-[180px] md:size-[140px] touch-none select-none cursor-pointer"
        aria-label="Heading selector"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Outer + inner rings */}
        <circle cx="100" cy="100" r="92" fill="none" stroke="#475569" strokeWidth="1" />
        <circle cx="100" cy="100" r="80" fill="none" stroke="#334155" strokeWidth="1" />

        {/* 10° tick marks around the rim */}
        {Array.from({ length: 36 }).map((_, i) => {
          const deg = i * 10
          const isMajor = deg % 30 === 0
          const rad = ((deg - 90) * Math.PI) / 180
          const r1 = 92
          const r2 = isMajor ? 84 : 88
          const x1 = 100 + r1 * Math.cos(rad)
          const y1 = 100 + r1 * Math.sin(rad)
          const x2 = 100 + r2 * Math.cos(rad)
          const y2 = 100 + r2 * Math.sin(rad)
          return (
            <line
              key={deg}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={isMajor ? '#64748b' : '#475569'}
              strokeWidth={isMajor ? 1.2 : 0.6}
            />
          )
        })}

        {/* Cardinal labels (N E S W) */}
        <text x="100" y="22" textAnchor="middle" fill="#e2e8f0" fontFamily="monospace" fontSize="12" fontWeight="bold">N</text>
        <text x="184" y="105" textAnchor="middle" fill="#e2e8f0" fontFamily="monospace" fontSize="12" fontWeight="bold">E</text>
        <text x="100" y="188" textAnchor="middle" fill="#e2e8f0" fontFamily="monospace" fontSize="12" fontWeight="bold">S</text>
        <text x="16" y="105" textAnchor="middle" fill="#e2e8f0" fontFamily="monospace" fontSize="12" fontWeight="bold">W</text>

        {/* 30° labels (030 060 120 150 210 240 300 330) */}
        {[30, 60, 120, 150, 210, 240, 300, 330].map((deg) => {
          const rad = ((deg - 90) * Math.PI) / 180
          const r = 70
          const x = 100 + r * Math.cos(rad)
          const y = 100 + r * Math.sin(rad) + 3
          const label = String(deg).padStart(3, '0')
          return (
            <text
              key={deg}
              x={x}
              y={y}
              textAnchor="middle"
              fill="#94a3b8"
              fontFamily="monospace"
              fontSize="8"
            >
              {label}
            </text>
          )
        })}

        {/* Current-heading pointer — small white triangle on inner ring */}
        {renderCurrentPointer(props.currentHeadingDeg)}

        {/* Target heading bug — orange marker on outer ring */}
        {renderBug(bug)}

        {/* Center readout — tap to focus the hidden numeric input,
            which pops the native mobile numeric keyboard. */}
        <g
          onPointerDown={(e) => {
            // Stop the SVG's drag handler from firing on the readout.
            e.stopPropagation()
            focusNumericInput()
          }}
          style={{ cursor: 'pointer' }}
        >
          <rect x="64" y="80" width="72" height="40" rx="5" fill="#1e293b" stroke="#0284c7" strokeWidth="1.2" />
          <text
            x="100"
            y="108"
            textAnchor="middle"
            fill="#f59e0b"
            fontFamily="monospace"
            fontSize="22"
            fontWeight="bold"
          >
            {(rawInput ?? String(bug).padStart(3, '0')).padStart(3, '0')}°
          </text>
        </g>
      </svg>

      {/* Buttons row 1: Turn left / Turn right */}
      <div className="mt-2 flex w-full gap-2">
        <button
          type="button"
          disabled={!dirty}
          onClick={() => props.onSubmit(bug, 'left')}
          className="flex-1 rounded-md bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 shadow hover:bg-slate-600 disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-500"
        >
          ↺ Turn left
        </button>
        <button
          type="button"
          disabled={!dirty}
          onClick={() => props.onSubmit(bug, 'right')}
          className="flex-1 rounded-md bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 shadow hover:bg-slate-600 disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-500"
        >
          Turn right ↻
        </button>
      </div>

      {/* Buttons row 2: Confirm */}
      <button
        type="button"
        disabled={!dirty}
        onClick={() => props.onSubmit(bug, 'shortest')}
        className="mt-2 w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
      >
        Confirm {String(bug).padStart(3, '0')}°
      </button>
    </div>
  )
}

function renderCurrentPointer(deg: number) {
  // Small white triangle on inner ring at the current heading.
  const rad = ((deg - 90) * Math.PI) / 180
  const r = 76
  const x = 100 + r * Math.cos(rad)
  const y = 100 + r * Math.sin(rad)
  // Tangent direction so the triangle points inward.
  const tx = -Math.sin(rad)
  const ty = Math.cos(rad)
  // Inward-pointing triangle: tip toward center, base perpendicular.
  const tipX = x - 8 * Math.cos(rad)
  const tipY = y - 8 * Math.sin(rad)
  const baseLX = x + 4 * tx
  const baseLY = y + 4 * ty
  const baseRX = x - 4 * tx
  const baseRY = y - 4 * ty
  return (
    <polygon
      points={`${tipX},${tipY} ${baseLX},${baseLY} ${baseRX},${baseRY}`}
      fill="#e2e8f0"
      opacity="0.9"
    />
  )
}

function renderBug(deg: number) {
  // Chunky orange bug straddling the outer ring at the target heading.
  // Two pieces: a rounded rect on the rim + a triangle pointing inward.
  const rad = ((deg - 90) * Math.PI) / 180
  const r = 92
  const x = 100 + r * Math.cos(rad)
  const y = 100 + r * Math.sin(rad)
  // Triangle tip points toward center.
  const tipX = x - 6 * Math.cos(rad)
  const tipY = y - 6 * Math.sin(rad)
  // Triangle base sits flush with the rim.
  const tx = -Math.sin(rad)
  const ty = Math.cos(rad)
  const baseLX = x + 5 * tx
  const baseLY = y + 5 * ty
  const baseRX = x - 5 * tx
  const baseRY = y - 5 * ty
  // Outer rectangle sits behind the triangle for the chunky-bug look.
  // Position centered on the rim, rotated to match the bearing.
  const rectW = 14
  const rectH = 12
  return (
    <g>
      <rect
        x={x - rectW / 2}
        y={y - rectH / 2}
        width={rectW}
        height={rectH}
        rx={2}
        fill="#f59e0b"
        transform={`rotate(${deg} ${x} ${y})`}
      />
      <polygon
        points={`${tipX},${tipY} ${baseLX},${baseLY} ${baseRX},${baseRY}`}
        fill="#fbbf24"
      />
    </g>
  )
}
